import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { BidResults } from './BidResults';

/**
 * Mention component for rendering @mentions with styling and tooltip.
 */
function Mention({ participantId, children }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const name = children;

  return (
    <span
      className="mention"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      @{name}
      {showTooltip && (
        <span className="mention-tooltip">
          <span className="mention-tooltip-name">{name}</span>
          <span className="mention-tooltip-type">Participant</span>
        </span>
      )}
    </span>
  );
}

/**
 * Custom link renderer that handles participant: protocol for mentions.
 * Regular links are rendered normally; participant: links render as styled mentions.
 */
function createMarkdownComponents() {
  return {
    a: ({ href, children }) => {
      // Check if this is a mention link (participant: protocol)
      if (href && href.startsWith('participant:')) {
        const participantId = href.replace('participant:', '');
        return <Mention participantId={participantId}>{children}</Mention>;
      }

      // Regular link - render normally with security attributes
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
  };
}

/**
 * Expandable prompt details component for generated images.
 * Shows the original prompt with option to expand and see full prompt sent.
 */
function PromptDetails({ prompt, fullPromptSent, modelUsed }) {
  const [expanded, setExpanded] = useState(false);
  const hasFullPrompt = fullPromptSent && fullPromptSent !== prompt;

  return (
    <div className="image-prompt-details">
      <div className="image-caption image-prompt">
        <span className="prompt-label">Prompt:</span> {prompt}
        {hasFullPrompt && (
          <button
            className="prompt-expand-btn"
            onClick={() => setExpanded(!expanded)}
            title={expanded ? "Hide full prompt" : "Show full prompt with injected style/context"}
          >
            {expanded ? '▼' : '▶'} Full prompt
          </button>
        )}
      </div>
      {expanded && hasFullPrompt && (
        <div className="full-prompt-details">
          <div className="full-prompt-header">
            <span className="full-prompt-label">Full prompt sent to {modelUsed || 'image model'}:</span>
          </div>
          <pre className="full-prompt-content">{fullPromptSent}</pre>
        </div>
      )}
    </div>
  );
}

/**
 * Renders a single content block (text, image, or image_request).
 */
function ContentBlock({ block, index, isGenerating }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  switch (block.type) {
    case 'text':
      return (
        <div key={index} className="content-block content-block-text">
          {isGenerating ? (
            // Plain text during streaming to avoid markdown parsing mid-stream
            block.content
          ) : (
            // Markdown rendering for completed messages - wrap to override white-space
            <div className="markdown-rendered">
              <ReactMarkdown
                remarkPlugins={[remarkBreaks]}
                components={createMarkdownComponents()}
              >
                {block.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      );

    case 'image':
      return (
        <div key={index} className="content-block content-block-image">
          {!imageLoaded && !imageError && (
            <div className="image-loading">Loading image...</div>
          )}
          {imageError && (
            <div className="image-error">Failed to load image</div>
          )}
          <img
            src={block.url}
            alt={block.alt || 'Generated image'}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            style={{ display: imageLoaded ? 'block' : 'none' }}
            className="generated-image"
          />
          {block.alt && <div className="image-caption">{block.alt}</div>}
        </div>
      );

    case 'image_request':
      // Image generation request - show status
      if (block.generated && block.image_url) {
        // Image has been generated
        return (
          <div key={index} className="content-block content-block-image">
            {!imageLoaded && !imageError && (
              <div className="image-loading">Loading image...</div>
            )}
            {imageError && (
              <div className="image-error">Failed to load image</div>
            )}
            <img
              src={block.image_url}
              alt={block.prompt || 'Generated image'}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              style={{ display: imageLoaded ? 'block' : 'none' }}
              className="generated-image"
            />
            <PromptDetails
              prompt={block.prompt}
              fullPromptSent={block.full_prompt_sent}
              modelUsed={block.model_used}
            />
          </div>
        );
      } else if (block.error) {
        // Image generation failed
        return (
          <div key={index} className="content-block content-block-image-error">
            <div className="image-generation-error">
              <span className="error-icon">⚠️</span>
              <span className="error-message">
                {block.error === 'content_policy'
                  ? 'Image could not be generated (content policy)'
                  : `Image generation failed: ${block.error_message || block.error}`}
              </span>
            </div>
            <div className="image-prompt-failed">
              <span className="prompt-label">Prompt:</span> {block.prompt}
            </div>
          </div>
        );
      } else {
        // Image is still being generated
        return (
          <div key={index} className="content-block content-block-image-pending">
            <div className="image-generating">
              <span className="generating-spinner">🎨</span>
              <span className="generating-text">Generating image...</span>
            </div>
            <div className="image-prompt-pending">
              <span className="prompt-label">Prompt:</span> {block.prompt}
            </div>
          </div>
        );
      }

    default:
      // Unknown block type - render as text
      return (
        <div key={index} className="content-block content-block-unknown">
          {JSON.stringify(block)}
        </div>
      );
  }
}

/**
 * Renders message content - either rich content_blocks or legacy text.
 */
function MessageContent({ message, isGenerating }) {
  // Use content_blocks if available, otherwise fall back to plain content
  const blocks = message.content_blocks;

  if (blocks && Array.isArray(blocks) && blocks.length > 0) {
    return (
      <div className="message-content message-content-rich">
        {blocks.map((block, index) => (
          <ContentBlock key={index} block={block} index={index} isGenerating={isGenerating} />
        ))}
      </div>
    );
  }

  // Legacy: plain text content (also used during streaming)
  return (
    <div className="message-content">
      {isGenerating ? (
        // Plain text during streaming
        message.content
      ) : (
        // Markdown rendering for completed messages - wrap in div to override white-space
        <div className="markdown-rendered">
          <ReactMarkdown
            remarkPlugins={[remarkBreaks]}
            components={createMarkdownComponents()}
          >
            {message.content}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
}

export function Message({ message, bidResults }) {
  const authorName = message.author?.name || 'Unknown';
  const timestamp = new Date(message.created_at).toLocaleTimeString();
  const isLlm = message.author?.participant_type === 'llm';
  const metadata = message.metadata || {};
  const hasBidInfo = isLlm && metadata.bid_score !== undefined;
  const isGenerating = message.status === 'generating';

  // Show bid results above LLM messages (the cycle that led to this response)
  const showBidResults = isLlm && bidResults;

  return (
    <div className={`message ${isLlm ? 'message-llm' : 'message-human'} ${isGenerating ? 'message-generating' : ''}`}>
      <div className="message-avatar">
        {authorName.charAt(0).toUpperCase()}
      </div>
      <div className="message-body">
        {/* Show bid results that led to this LLM message */}
        {showBidResults && (
          <BidResults bidResults={bidResults} compact />
        )}
        <div className="message-header">
          <span className="message-author">{authorName}</span>
          <span className="message-timestamp">{timestamp}</span>
          {isGenerating && (
            <span className="message-generating-label">generating...</span>
          )}
        </div>
        {hasBidInfo && (
          <div className="message-bid-info">
            <span className="bid-score">bid: {metadata.bid_score}</span>
            {metadata.decay_multiplier && metadata.decay_multiplier < 1 && (
              <span className="repetition-modifier">
                × {metadata.decay_multiplier.toFixed(2)}
              </span>
            )}
            <span className="effective-priority">
              → {Math.round(metadata.final_priority)}
            </span>
          </div>
        )}
        <div className="message-content-wrapper">
          <MessageContent message={message} isGenerating={isGenerating} />
          {isGenerating && (
            <span className="generating-cursor">▊</span>
          )}
        </div>
      </div>
    </div>
  );
}
